import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useEvents, useMasters } from '../../lib/data/queries.js';
import { EmptyHint, palette } from '../../components/design.js';

interface CleaningSchedule {
  id: string;
  title: string;
  frequency: string;
}

interface CleaningEvent {
  id: string;
  payload: { schedule_id: string };
  client_ts: string;
}

const DAYS_BACK = 30;

export function CleaningDonePage(): JSX.Element {
  const { t } = useTranslation();
  const { data: schedules = [] } = useMasters<CleaningSchedule>('cleaning_schedules');
  const { data: events = [] } = useEvents('cleaning_done', 500, true);

  const { data: corrections = [] } = useEvents('cleaning_voided', null);
  const cancelled = new Set(corrections.flatMap((event) => Array.isArray(event.payload.original_client_uuids) ? event.payload.original_client_uuids as string[] : []));

  const scheduleById = new Map<string, CleaningSchedule>();
  for (const s of schedules) scheduleById.set(s.id, s);

  const cutoff = Date.now() - DAYS_BACK * 86_400_000;
  const recent = (events as CleaningEvent[])
    .filter((e) => Date.parse(e.client_ts) >= cutoff)
    .sort((a, b) => Date.parse(b.client_ts) - Date.parse(a.client_ts));

  const dtf = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/haccp/cleaning" />
          </IonButtons>
          <IonTitle>Completati ({DAYS_BACK}gg)</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 24px' }}>
          {recent.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
              <EmptyHint>{t('common.empty')}</EmptyHint>
            </div>
          ) : (
            recent.map((e) => {
              const s = scheduleById.get(e.payload.schedule_id);
              return (
                <div
                  key={e.id}
                  style={{
                    background: palette.card,
                    borderRadius: 14,
                    borderLeft: `4px solid ${palette.success}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    margin: '10px 0',
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 8,
                      background: palette.success,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IonIcon icon={checkmarkOutline} style={{ color: '#fff', fontSize: 16 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: palette.text }}>
                      {cancelled.has(e.id) ? 'Annullata · ' : ''}{s?.title ?? e.payload.schedule_id.slice(0, 8)}
                    </div>
                    <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>
                      {s?.frequency ?? ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: palette.textFaint, flexShrink: 0 }}>
                    {dtf.format(Date.parse(e.client_ts))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
